import { useReducedMotion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { PageHero, PageCta } from "../components/PageHero";
import { Reveal } from "../components/motion";

const CYCLE_CIRCUMFERENCE = 2 * Math.PI * 96;

const OrganisationCycle = ({ p }) => {
  const reduce = useReducedMotion();
  const strategy = p.dims[0];
  const culture = p.dims[1];
  const structure = p.dims[2];

  return (
    <div data-testid="organisation-cycle" className="border border-ink/10 bg-white p-6 sm:p-10">
      <style>{`
        .org-cycle-progress {
          stroke-dasharray: ${CYCLE_CIRCUMFERENCE};
          stroke-dashoffset: ${CYCLE_CIRCUMFERENCE};
          animation: org-cycle-draw 16s linear infinite;
        }
        @keyframes org-cycle-draw {
          0% { stroke-dashoffset: ${CYCLE_CIRCUMFERENCE}; stroke: #032C37; opacity: 1; animation-timing-function: cubic-bezier(0.42, 0, 0.2, 1); }
          27% { stroke-dashoffset: ${(CYCLE_CIRCUMFERENCE * 2) / 3}; animation-timing-function: cubic-bezier(0.42, 0, 0.2, 1); }
          54% { stroke-dashoffset: ${CYCLE_CIRCUMFERENCE / 3}; animation-timing-function: cubic-bezier(0.42, 0, 0.2, 1); }
          81% { stroke-dashoffset: 0; stroke: #032C37; animation-timing-function: ease-in-out; }
          88% { stroke-dashoffset: 0; stroke: #629AB9; opacity: 1; animation-timing-function: ease-in-out; }
          93% { stroke-dashoffset: 0; stroke: #629AB9; opacity: 1; animation-timing-function: ease-out; }
          97% { stroke-dashoffset: 0; stroke: #629AB9; opacity: 0; }
          99% { stroke-dashoffset: ${CYCLE_CIRCUMFERENCE}; stroke: #032C37; opacity: 0; }
          100% { stroke-dashoffset: ${CYCLE_CIRCUMFERENCE}; stroke: #032C37; opacity: 1; }
        }
      `}</style>
      <div
        className="relative mx-auto aspect-[21/16] w-full max-w-[620px]"
        role="img"
        aria-label={`${strategy} → ${structure} → ${culture} → ${strategy}`}
      >
        <svg viewBox="0 0 420 320" className="h-full w-full" aria-hidden="true">
          <circle cx="210" cy="165" r="96" fill="none" stroke="#032C37" strokeOpacity="0.12" strokeWidth="24" />
          {reduce ? (
            <circle cx="210" cy="165" r="96" fill="none" stroke="#629AB9" strokeWidth="24" />
          ) : (
            <circle
              className="org-cycle-progress"
              cx="210"
              cy="165"
              r="96"
              fill="none"
              stroke="#032C37"
              strokeWidth="24"
              strokeLinecap="round"
              transform="rotate(-90 210 165)"
            />
          )}
        </svg>
        <span data-testid="organisation-cycle-label-strategie" className="absolute left-1/2 top-[2%] -translate-x-1/2 text-[11px] font-medium uppercase tracking-label text-ink/70 sm:text-[13px]">
          {strategy}
        </span>
        <span data-testid="organisation-cycle-label-struktur" className="absolute bottom-[4%] left-[72%] text-[11px] font-medium uppercase tracking-label text-ink/70 sm:text-[13px]">
          {structure}
        </span>
        <span data-testid="organisation-cycle-label-kultur" className="absolute bottom-[4%] right-[72%] text-right text-[11px] font-medium uppercase tracking-label text-ink/70 sm:text-[13px]">
          {culture}
        </span>
      </div>
    </div>
  );
};

export default function Organisation() {
  const { t } = useLanguage();
  const p = t.pages.organisation;
  return (
    <main data-testid="organisation-page">
      <PageHero testId="organisation-hero" label={p.label} title={p.title} sub={p.sub} />

      <section data-testid="organisation-journey" className="bg-paper">
        <div className="mx-auto max-w-[1600px] px-6 py-28 sm:px-12 sm:py-40 md:px-16">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal y={14}>
                <p className="text-[13px] font-medium uppercase tracking-label text-wave">{p.journeyLabel}</p>
              </Reveal>
              <Reveal delay={0.15}>
                <h2 className="font-head mt-8 text-3xl leading-tight text-ink sm:text-5xl">{p.journeyTitle}</h2>
              </Reveal>
              <Reveal delay={0.3}>
                <p className="mt-8 text-base font-light leading-relaxed text-ink/70 sm:text-lg">{p.journeyText}</p>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="lg:col-span-7">
              <OrganisationCycle p={p} />
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="organisation-experience" className="grain relative overflow-hidden bg-deep text-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 sm:px-12 sm:py-40 md:px-16">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal y={14}>
                <p className="text-[13px] font-medium uppercase tracking-label text-wave">{p.expLabel}</p>
              </Reveal>
              <Reveal delay={0.15}>
                <h2 className="font-head mt-8 text-3xl leading-tight sm:text-5xl">{p.expTitle}</h2>
              </Reveal>
              <Reveal delay={0.3}>
                <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/65 sm:text-lg">
                  {p.expText}
                </p>
              </Reveal>
            </div>
            <div className="flex flex-col justify-center lg:col-span-5 lg:col-start-8">
              <Reveal delay={0.2}>
                <span data-testid="org-big-number" className="font-head text-8xl text-wave sm:text-9xl">
                  {p.expBig}
                </span>
                <p className="mt-6 max-w-sm text-base font-light leading-relaxed text-white/70">{p.expBigText}</p>
              </Reveal>
            </div>
          </div>
          <Reveal delay={0.2}>
            <p className="mt-20 max-w-3xl border-l-2 border-wave pl-8 text-lg font-light leading-relaxed text-white/80 sm:text-xl">
              {p.expCta}
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="organisation-work" className="bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-24 sm:px-12 sm:py-32 md:px-16">
          <Reveal>
            <h2 className="font-head max-w-3xl text-3xl leading-tight text-ink sm:text-4xl">{p.workTitle}</h2>
          </Reveal>
          <div className="mt-16 grid auto-rows-fr gap-x-12 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {p.workItems.map((topic, i) => (
              <Reveal key={topic} delay={i * 0.05} className="group h-full">
                <div
                  data-testid={`organisation-work-item-0${i + 1}`}
                  className="flex h-full items-baseline gap-4 border-b border-ink/10 pb-6 transition-colors duration-500 group-hover:border-wave/70"
                >
                  <span className="font-head text-base text-wave/85 transition-colors duration-500 group-hover:text-wave">
                    0{i + 1}
                  </span>
                  <span className="text-base font-light leading-relaxed text-ink/80 transition-colors duration-500 group-hover:text-ink sm:text-lg">
                    {topic}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <PageCta title={p.ctaTitle} text={p.ctaText} />
    </main>
  );
}
